import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ForgotUserId from "@/pages/ForgotUserId";
import ResetPassword from "@/pages/ResetPassword";
import PayInvoice from "@/pages/PayInvoice";
import Track from "@/pages/Track";
import ClientApproval from "@/pages/ClientApproval";
import Layout from "@/pages/Layout";
import Dashboard from "@/pages/Dashboard";
import BankPerformance from "@/pages/dashboard/BankPerformance";
import Alerts from "@/pages/dashboard/Alerts";
import QuickActions from "@/pages/dashboard/QuickActions";
import Activity from "@/pages/Activity";
import ActivityExport from "@/pages/activity/ActivityExport";
import Branches from "@/pages/Branches";
import BankMaster from "@/pages/branches/BankMaster";
import Regions from "@/pages/branches/Regions";
import Empanelment from "@/pages/branches/Empanelment";
import BranchProfile from "@/pages/branches/BranchProfile";
import Clients from "@/pages/Clients";
import ClientGroups from "@/pages/clients/ClientGroups";
import ImportClients from "@/pages/clients/ImportClients";
import ClientProfile from "@/pages/clients/ClientProfile";
import Jobs from "@/pages/Jobs";
import Gis from "@/pages/Gis";
import Records from "@/pages/Records";
import Workload from "@/pages/team/Workload";
import MyJobs from "@/pages/MyJobs";
import SiteVisitCalendar from "@/pages/jobs/SiteVisitCalendar";
import QcQueue from "@/pages/jobs/QcQueue";
import RevisionJobs from "@/pages/jobs/RevisionJobs";
import ReportTemplates from "@/pages/jobs/ReportTemplates";
import ReportFormats from "@/pages/jobs/ReportFormats";
import PropertyTypes from "@/pages/jobs/PropertyTypes";
import Invoices from "@/pages/Invoices";
import PaymentTracking from "@/pages/invoices/PaymentTracking";
import Outstanding from "@/pages/invoices/Outstanding";
import BulkInvoices from "@/pages/invoices/BulkInvoices";
import CreditNotes from "@/pages/invoices/CreditNotes";
import TaxSettings from "@/pages/invoices/TaxSettings";
import BillingReports from "@/pages/invoices/BillingReports";
import Followups from "@/pages/Followups";
import Snoozed from "@/pages/followups/Snoozed";
import CompletedHistory from "@/pages/followups/CompletedHistory";
import ReminderSettings from "@/pages/followups/ReminderSettings";
import Campaigns from "@/pages/outreach/Campaigns";
import NewCampaign from "@/pages/outreach/NewCampaign";
import OutreachTemplates from "@/pages/outreach/OutreachTemplates";
import ResponseTracking from "@/pages/outreach/ResponseTracking";
import LeadSuggestions from "@/pages/outreach/LeadSuggestions";
import OutreachAnalytics from "@/pages/outreach/OutreachAnalytics";
import Outreach from "@/pages/Outreach";
import Team from "@/pages/Team";
import RolesPermissions from "@/pages/team/RolesPermissions";
import ValuerProfiles from "@/pages/team/ValuerProfiles";
import Availability from "@/pages/team/Availability";
import TeamActivityLog from "@/pages/team/TeamActivityLog";
import DraftAssignments from "@/pages/team/DraftAssignments";
import TeamNotifications from "@/pages/team/TeamNotifications";
import StatutoryReports from "@/pages/StatutoryReports";
import Account from "@/pages/Account";
import FirmSettings from "@/pages/account/FirmSettings";
import NotificationPrefs from "@/pages/account/NotificationPrefs";
import Integrations from "@/pages/account/Integrations";
import Security from "@/pages/account/Security";
import DataBackup from "@/pages/account/DataBackup";
import Employees from "@/pages/Employees";
import PipelineDashboard from "@/pages/PipelineDashboard";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground font-mono text-sm">Loading…</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function OwnerOnly({ children }) {
  const { user } = useAuth();
  return user?.role === "owner" ? children : <Navigate to="/app" replace />;
}

function HomeIndex() {
  const { user } = useAuth();
  if (user?.role === "owner") return <Dashboard />;
  if (user?.role === "accountant") return <Invoices />;
  return <MyJobs />;
}

function AllowRoles({ roles, children }) {
  const { user } = useAuth();
  return roles.includes(user?.role) ? children : <Navigate to="/app" replace />;
}

const INVOICE_ROUTES = [
  ["invoices", Invoices],
  ["invoices/payments", PaymentTracking],
  ["invoices/outstanding", Outstanding],
  ["invoices/bulk", BulkInvoices],
  ["invoices/credit-notes", CreditNotes],
  ["invoices/tax-settings", TaxSettings],
  ["invoices/reports", BillingReports],
];

const OWNER_ROUTES = [
  ["dashboard/banks", BankPerformance],
  ["dashboard/alerts", Alerts],
  ["dashboard/quick-actions", QuickActions],
  ["activity", Activity],
  ["activity/export", ActivityExport],
  ["branches", Branches],
  ["branches/bank-master", BankMaster],
  ["branches/regions", Regions],
  ["branches/empanelment", Empanelment],
  ["branches/:id", BranchProfile],
  ["clients", Clients],
  ["clients/groups", ClientGroups],
  ["clients/import", ImportClients],
  ["clients/:id", ClientProfile],
  ["jobs/pipeline", PipelineDashboard],
  ["gis", Gis],
  ["jobs/qc", QcQueue],
  ["jobs/revisions", RevisionJobs],
  ["jobs/templates", ReportTemplates],
  ["jobs/formats", ReportFormats],
  ["jobs/property-types", PropertyTypes],
  ["statutory-reports", StatutoryReports],
  ["followups", Followups],
  ["followups/snoozed", Snoozed],
  ["followups/completed", CompletedHistory],
  ["followups/settings", ReminderSettings],
  ["outreach", Campaigns],
  ["outreach/new", NewCampaign],
  ["outreach/templates", OutreachTemplates],
  ["outreach/tracking", ResponseTracking],
  ["outreach/leads", LeadSuggestions],
  ["outreach/analytics", OutreachAnalytics],
  ["outreach/draft", Outreach],
  ["team", Team],
  ["team/workload", Workload],
  ["team/roles", RolesPermissions],
  ["team/valuers", ValuerProfiles],
  ["team/availability", Availability],
  ["team/drafts", DraftAssignments],
  ["team/notifications", TeamNotifications],
  ["team/log", TeamActivityLog],
  ["records", Records],
  ["account/firm", FirmSettings],
  ["account/integrations", Integrations],
  ["account/security", Security],
  ["account/backup", DataBackup],
];

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/forgot-userid" element={<ForgotUserId />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/pay/:id" element={<PayInvoice />} />
            <Route path="/track" element={<Track />} />
            <Route path="/approve/:id" element={<ClientApproval />} />
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<HomeIndex />} />
              <Route path="jobs" element={<AllowRoles roles={["owner", "report_writer"]}><Jobs /></AllowRoles>} />
              <Route path="my-jobs" element={<MyJobs />} />
              <Route path="employees" element={<Employees />} />
              <Route path="jobs/calendar" element={<SiteVisitCalendar />} />
              <Route path="account" element={<Account />} />
              <Route path="account/notifications" element={<NotificationPrefs />} />
              {OWNER_ROUTES.map(([path, Component]) => (
                <Route key={path} path={path} element={<OwnerOnly><Component /></OwnerOnly>} />
              ))}
              {INVOICE_ROUTES.map(([path, Component]) => (
                <Route key={path} path={path} element={<AllowRoles roles={["owner", "accountant"]}><Component /></AllowRoles>} />
              ))}
              <Route path="*" element={<Navigate to="/app" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
